import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Container, Row, Col, Button, Card, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { DonationFormData, CountryOption, StateOption } from '../../types/donation';
import { donationService } from '../../services/api';
import './DonationForm.css';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

// Luhn algorithm check for credit card numbers
const luhnCheck = (num: string): boolean => {
  const digits = (num || '').replace(/\D/g, '');
  let sum = 0;
  let shouldDouble = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = parseInt(digits.charAt(i), 10);
    if (Number.isNaN(d)) return false;
    if (shouldDouble) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    shouldDouble = !shouldDouble;
  }
  return digits.length > 0 && sum % 10 === 0;
};

interface DonationFormProps {
  entity?: string;
  emailAppName?: string;
  emailTemplateName?: string;
  title?: string;
}

const DonationForm: React.FC<DonationFormProps> = ({
  entity = 'RMS',
  emailAppName = 'Donation',
  emailTemplateName = 'RMSDonation',
  title,
}) => {
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [states, setStates] = useState<StateOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submissionStatus, setSubmissionStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [defaultCountryId, setDefaultCountryId] = useState<string>('');
  const [defaultStateId, setDefaultStateId] = useState<string>('');
  // reCAPTCHA state
  const [recaptchaToken, setRecaptchaToken] = useState<string>('');
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState<number | null>(null);
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  if (!RECAPTCHA_SITE_KEY && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('RECAPTCHA_SITE_KEY is not set. Define REACT_APP_RECAPTCHA_SITE_KEY in your .env file.');
  }

  // Load and render Google reCAPTCHA v2 (checkbox) widget
  useEffect(() => {
    const ensureScript = (): Promise<void> => {
      return new Promise((resolve) => {
        const w = window as any;
        if (w.grecaptcha) return resolve();
        const existing = document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]');
        if (existing) {
          existing.addEventListener('load', () => resolve());
          return;
        }
        const s = document.createElement('script');
        s.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        document.body.appendChild(s);
      });
    };

    const renderWidget = () => {
      const w = window as any;
      const el = document.getElementById('recaptcha-container');
      if (!el) return;
      if (recaptchaWidgetId !== null) return; // already rendered

      // Always use standard grecaptcha for v2 checkbox
      const pollUntilReady = () => {
        const api = (w && w.grecaptcha) ? w.grecaptcha : null;
        if (!api) {
          // Script not yet attached to window
          setTimeout(pollUntilReady, 150);
          return;
        }
        const doRender = () => {
          if (recaptchaWidgetId !== null) return;
          if (typeof api.render !== 'function') {
            // Not ready yet; try again shortly
            setTimeout(doRender, 150);
            return;
          }
          try {
            const id = api.render(el, {
              sitekey: RECAPTCHA_SITE_KEY,
              callback: (token: string) => setRecaptchaToken(token),
              'expired-callback': () => setRecaptchaToken(''),
              'error-callback': () => setRecaptchaToken(''),
            });
            setRecaptchaWidgetId(id);
          } catch (e) {
            // In case of intermittent readiness issues
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.warn('reCAPTCHA render failed, retrying...', e);
            }
            setTimeout(doRender, 200);
          }
        };

        if (typeof api.ready === 'function') {
          api.ready(doRender);
        } else {
          // Fallback if ready() is not available
          setTimeout(doRender, 0);
        }
      };

      pollUntilReady();
    };

    let mounted = true;
    ensureScript().then(() => {
      if (!mounted) return;
      // Defer a tick to ensure the container is mounted in DOM
      setTimeout(renderWidget, 0);
    });
    return () => {
      mounted = false;
    };
  }, [RECAPTCHA_SITE_KEY, recaptchaWidgetId]);

  // Initial form values
  const initialValues: DonationFormData = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNo: '',
    address: '',
    country: defaultCountryId || '',
    state: defaultStateId || '',
    city: '',
    postalCode: '',
    purpose: '',
    paymentPurpose: '',
    donationAmount: '',
    donationType: 'OneTime',
    cardNumber: '',
    cvv: '',
    expirationDate: ''
  };

  // Form validation schema
  const validationSchema = Yup.object({
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    phoneNo: Yup.string().required('Phone Number is required'),
    address: Yup.string().required('Address is required'),
    country: Yup.string().required('Country is required'),
    state: Yup.string().required('State is required'),
    city: Yup.string().required('City is required'),
    postalCode: Yup.string().required('Postal Code/ZIP is required'),
    purpose: Yup.string().required('Purpose of Donation is required'),
    paymentPurpose: Yup.string().required('Payment Purpose is required'),
    donationAmount: Yup.string()
      .matches(/^\d+(?:\.\d{1,2})?$/, 'Enter a valid amount (e.g., 10 or 10.50)')
      .test('greater-than-zero', 'Amount must be greater than 0', (val) => {
        if (!val) return false;
        const num = Number(val);
        return Number.isFinite(num) && num > 0;
      })
      .required('Donation Amount is required'),
    donationType: Yup.string().required('Donation Type is required'),
    cardNumber: Yup.string().required('Card Number is required')
      .test(
        'card-luhn-and-length',
        'Enter a valid card number (13–19 digits)',
        (val) => {
          const digits = (val || '').replace(/\D/g, '');
          if (!/^\d{13,19}$/.test(digits)) return false;
          return luhnCheck(digits);
        }
      ),
    cvv: Yup.string().required('CVV/CVC is required')
      .matches(/^[0-9]{3,4}$/, 'CVV must be 3 or 4 digits'),
    expirationDate: Yup.string()
      .required('Expiration Date is required')
      .matches(/^(0[1-9]|1[0-2])\/(20[2-9][0-9])$/, 'Format must be MM/YYYY')
      .test('exp-not-past', 'Expiration must be a future month', (val) => {
        if (!val) return false;
        const match = val.match(/^(0[1-9]|1[0-2])\/(20[2-9][0-9])$/);
        if (!match) return false;
        const month = parseInt(match[1], 10);
        const year = parseInt(match[2], 10);
        const now = new Date();
        const currentMonth = now.getMonth() + 1; // 1-12
        const currentYear = now.getFullYear();
        return year > currentYear || (year === currentYear && month > currentMonth);
      })
  });

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      const countryData = await donationService.getCountries();
      setCountries(countryData);
      // Determine USA as default if available
      const usa = countryData.find(c =>
        /^(united states|usa|united states of america)$/i.test(c.name)
      );
      const fallback = countryData[0];
      const selected = usa || fallback;
      if (selected) {
        setDefaultCountryId(selected.value);
        // Always fetch states by country ID (per requirement)
        const countryIdForStates = Number.parseInt(String(selected.value), 10) || Number(selected.id);
        const defaultStates = await donationService.getStatesByCountry(countryIdForStates);
        setStates(defaultStates);
        // Preselect Georgia if available
        const ga = defaultStates.find((s) => /^georgia$/i.test(s.name));
        if (ga) {
          setDefaultStateId(String(ga.id));
        } else if (defaultStates.length) {
          setDefaultStateId(String(defaultStates[0].id));
        }
      }
    };
    
    fetchCountries();
  }, []);

  // Fetch states when country changes
  const handleCountryChange = async (selectedCountry: any, formikProps: any) => {
    if (selectedCountry && selectedCountry.value) {
      formikProps.setFieldValue('country', selectedCountry.value);
      formikProps.setFieldValue('state', '');
      
      // Fetch states by selected country's ID
      const stateData = await donationService.getStatesByCountry(Number(selectedCountry.value));
      setStates(stateData);
      // Default to Georgia if present, else first state
      const ga = stateData.find((s) => /^georgia$/i.test(s.name));
      const chosen = ga || stateData[0];
      if (chosen) {
        formikProps.setFieldValue('state', String(chosen.id));
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (values: DonationFormData, formikHelpers?: any) => {
    setLoading(true);
    setSubmissionStatus(null);
    // Require a valid reCAPTCHA token before submitting
    if (!recaptchaToken) {
      setLoading(false);
      setSubmissionStatus({ success: false, message: 'Please verify reCAPTCHA before submitting.' });
      return;
    }
    // Server-side verify the token
    const recaptchaResult = await donationService.verifyRecaptcha(recaptchaToken);
    if (!recaptchaResult || recaptchaResult.success !== true) {
      setLoading(false);
      setSubmissionStatus({ success: false, message: 'reCAPTCHA verification failed. Please try again.' });
      try {
        const w = window as any;
        if (recaptchaWidgetId !== null && w.grecaptcha) {
          w.grecaptcha.reset(recaptchaWidgetId);
          setRecaptchaToken('');
        }
      } catch {}
      return;
    }
    
    try {
      // Helpers to map ids to labels for payload
      const countryLabel = countries.find(c => String(c.value) === String(values.country))?.label || '';
      const stateLabel = states.find(s => String(s.value) === String(values.state))?.label || '';
      const formatPhone = (p: string) => (p || '').replace(/\D/g, '');

      // Build MVC-like payload shape
      const description = `${entity} Donation for user: ${values.firstName} ${values.lastName} camp name: Camp amount  of : ${values.donationAmount}`;
      const dataarray: any = {
        app: 'Camp',
        entity: entity,
        paymentType: 'cc',
        customerEmail: values.email,
        orderid: '0',
        amount: String(values.donationAmount),
        description,
        customer: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: formatPhone(values.phoneNo),
          company: 'string',
          notes: description,
          customerAccount: 'string',
          shippingSameAsBilling: true,
          billingAddress: {
            streetAddress1: values.address,
            streetAddress2: '-',
            city: values.city,
            stateCode: stateLabel,
            zipCode: values.postalCode,
            country: countryLabel,
          },
          shippingAddress: {
            streetAddress1: values.address,
            streetAddress2: '-',
            city: values.city,
            stateCode: stateLabel,
            zipCode: values.postalCode,
            country: countryLabel,
          },
        },
        creditcard: {
          creditCardNumber: values.cardNumber,
          expirationDate: values.expirationDate,
          issuer: 'string',
          billingZipCode: values.postalCode,
          customerId: 'string',
          cvv: values.cvv,
        },
        ach: {
          isCheckingAccount: 'string',
          routingNumber: 'string',
          accountNumber: 'string',
          bankName: 'string',
          customerId: 'string',
          isDefault: 'string',
        },
        // Always include recurr block; will be populated for Recurring and kept as placeholders otherwise
        recurr: {
          paymentAmount: 'string',
          accountId: 'string',
          startDate: 'string',
          executionFrequencyType: 'string',
          executionFrequencyParameter: 'string',
          endDate: 'string',
          paymentSubType: 'string',
          invoiceNumber: 'string',
          firstPaymentAmount: 'string',
          firstPaymentDate: 'string',
          description: 'string',
        },
      };

      if (values.donationType === 'Recurring') {
        const now = new Date();
        const oneYearLater = new Date(now);
        oneYearLater.setFullYear(now.getFullYear() + 1);
        dataarray.recurr.paymentAmount = String(values.donationAmount);
        dataarray.recurr.accountId = 'string';
        dataarray.recurr.startDate = now.toISOString();
        dataarray.recurr.executionFrequencyType = 'Monthly';
        dataarray.recurr.executionFrequencyParameter = '1';
        dataarray.recurr.endDate = oneYearLater.toISOString();
        dataarray.recurr.paymentSubType = 'string';
        dataarray.recurr.invoiceNumber = 'string';
        dataarray.recurr.firstPaymentAmount = String(values.donationAmount);
        dataarray.recurr.firstPaymentDate = now.toISOString();
        dataarray.recurr.description = description;
      }

      // Route to correct payment API with MVC-like payload
      const studentId: string = '0';
      const paymentResp: any = values.donationType === 'Recurring'
        ? await donationService.authNetRecurringPayment(dataarray, studentId)
        : await donationService.authNetOneTimePayment(dataarray);

      // Normalize success for both one-time and recurring responses
      const respMessages = (
        paymentResp?.messages ||
        paymentResp?.Messages ||
        paymentResp?.createTransactionResponse?.messages ||
        paymentResp?.message ||
        paymentResp?.Message ||
        {}
      );
      const resultCode = String(
        respMessages?.resultCode ?? paymentResp?.resultCode ?? ''
      );
      const msgArray = Array.isArray((respMessages as any)?.message)
        ? (respMessages as any).message
        : ((respMessages as any)?.message ? [(respMessages as any).message] : []);
      const messageCode = (msgArray[0]?.code) || (paymentResp?.createTransactionResponse?.messages?.message?.code);
      let messageText = '';
      if (typeof respMessages === 'string') {
        messageText = respMessages;
      } else {
        messageText = (msgArray[0]?.text || msgArray[0]?.description) || '';
      }
      // Fallbacks for top-level string messages
      if (!messageText && typeof (paymentResp as any)?.Message === 'string') {
        messageText = (paymentResp as any).Message;
      }
      if (!messageText && typeof (paymentResp as any)?.message === 'string') {
        messageText = (paymentResp as any).message;
      }
      const hasSubscriptionId = typeof paymentResp?.aRBCreateSubscriptionResponse?.subscriptionId === 'string' && paymentResp.aRBCreateSubscriptionResponse.subscriptionId.length > 0;
      const ok = (
        paymentResp?.success === true ||
        paymentResp?.isSuccess === true ||
        paymentResp?.status === 'OK' ||
        ['ok','OK','Ok'].includes(resultCode) ||
        messageCode === 'I00001' ||
        (values.donationType === 'Recurring' && hasSubscriptionId)
      );

      if (ok) {
        // Send confirmation email
        await donationService.sendEmailMessage(
          {
            Email: values.email,
            ContactNo: values.phoneNo,
            UserName: `${values.firstName} ${values.lastName}`.trim(),
            DonationDate: new Date().toISOString(),
            Amount: String(values.donationAmount),
            Address: values.address,
          },
          { appName: emailAppName, templateName: emailTemplateName, orgId: 3 }
        );

        // SweetAlert2 success popup
        if (typeof window !== 'undefined') {
          const extra = (values.donationType === 'Recurring' && hasSubscriptionId)
            ? ` Subscription ID: ${paymentResp.aRBCreateSubscriptionResponse.subscriptionId}.`
            : '';
          Swal.fire({
            icon: 'success',
            title: 'Thank you!',
            text: `Your donation was successful and a confirmation email has been sent.${extra}`,
            confirmButtonColor: '#28a745',
            confirmButtonText: 'Great',
            customClass: { popup: 'swal2-modern-popup' }
          });
        }

        const successMsg = (values.donationType === 'Recurring' && hasSubscriptionId)
          ? `Recurring donation successfully created. Subscription ID: ${paymentResp.aRBCreateSubscriptionResponse.subscriptionId}.`
          : 'Donation successfully processed. Thank you!';
        setSubmissionStatus({ success: true, message: successMsg });
        // Reset the form to initial values
        if (formikHelpers && typeof formikHelpers.resetForm === 'function') {
          formikHelpers.resetForm();
        }
        // Reset reCAPTCHA after successful submission
        try {
          const w = window as any;
          if (recaptchaWidgetId !== null && w.grecaptcha) {
            w.grecaptcha.reset(recaptchaWidgetId);
            setRecaptchaToken('');
          }
        } catch {}
      } else {
        const errMsg = messageText || 'Failed to process donation. Please verify your details and try again.';
        setSubmissionStatus({ success: false, message: errMsg });
      }
    } catch (error: any) {
      // Try to surface a useful error message from API response (Axios error shapes)
      let apiMessage = 'An error occurred while processing your donation. Please try again later.';
      try {
        const data = error?.response?.data ?? error?.data ?? null;
        if (data) {
          // Common message containers
          const msgs = (data as any).messages
            || (data as any).Messages
            || (data as any).message
            || (data as any).Message
            || (data as any)?.createTransactionResponse?.messages
            || (data as any)?.aRBCreateSubscriptionResponse?.messages;

          if (typeof msgs === 'string') {
            apiMessage = msgs;
          } else if ((msgs as any)?.message || (msgs as any)?.Message) {
            const inner = (msgs as any).message ?? (msgs as any).Message;
            const arr = Array.isArray(inner) ? inner : [inner];
            const first = arr[0] || {};
            apiMessage = first.text || first.description || first.message || first.Message || apiMessage;
          }

          // Authorize.Net specific error block
          const anetErr = (data as any)?.createTransactionResponse?.errors?.error;
          if (!apiMessage && anetErr) {
            const arr = Array.isArray(anetErr) ? anetErr : [anetErr];
            apiMessage = arr[0]?.errorText || arr[0]?.text || apiMessage;
          }

          // ARB subscription specific messages
          const arbMsg = (data as any)?.aRBCreateSubscriptionResponse?.messages?.message;
          if (!apiMessage && arbMsg) {
            const arr = Array.isArray(arbMsg) ? arbMsg : [arbMsg];
            apiMessage = arr[0]?.text || arr[0]?.description || apiMessage;
          }

          // Generic errors array
          if (!apiMessage && Array.isArray((data as any)?.errors) && (data as any).errors.length) {
            const e0 = (data as any).errors[0];
            apiMessage = (e0 && (e0.message || e0.error || (typeof e0 === 'string' ? e0 : ''))) || apiMessage;
          }

          // Capitalized or lowercase top-level Message fields
          if (!apiMessage && typeof (data as any)?.Message === 'string') {
            apiMessage = (data as any).Message;
          }
          if (!apiMessage && typeof (data as any)?.message === 'string') {
            apiMessage = (data as any).message;
          }

          // Plain string response body
          if (!apiMessage && typeof data === 'string') {
            apiMessage = data;
          }
        } else if (typeof error?.message === 'string') {
          apiMessage = error.message;
        }
      } catch {}

      setSubmissionStatus({ success: false, message: apiMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="donation-form-container">
      {/* Modern banner header */}
      {/* <div className="banner">
        <h1>{title || 'Make a Difference Today'}</h1>
        <p className="subtitle">Your generosity helps us serve the community.</p>
      </div> */}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formikProps) => (
          <Form>
            <Row>
              {/* Personal Details Card */}
              <Col md={6}>
                <Card className="mb-4 elevated-card">
                  <Card.Header>Please Enter Personal Details</Card.Header>
                  <Card.Body>
                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="firstName">First Name *</label>
                        <Field 
                          name="firstName"
                          type="text"
                          className="form-control"
                          placeholder="First Name"
                        />
                        <ErrorMessage name="firstName" component="div" className="text-danger" />
                      </Col>
                      <Col md={6}>
                        <label htmlFor="lastName">Last Name *</label>
                        <Field 
                          name="lastName"
                          type="text"
                          className="form-control"
                          placeholder="Last Name"
                        />
                        <ErrorMessage name="lastName" component="div" className="text-danger" />
                      </Col>
                    </Row>
                    
                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="email">Email *</label>
                        <Field 
                          name="email"
                          type="email"
                          className="form-control"
                          placeholder="Email"
                        />
                        <ErrorMessage name="email" component="div" className="text-danger" />
                      </Col>
                      <Col md={6}>
                        <label htmlFor="phoneNo">Phone No *</label>
                        <Field 
                          name="phoneNo"
                          type="text"
                          className="form-control"
                          placeholder="Phone No"
                        />
                        <ErrorMessage name="phoneNo" component="div" className="text-danger" />
                      </Col>
                    </Row>
                    
                    <Row className="mb-3">
                      <Col md={12}>
                        <label htmlFor="address">Address *</label>
                        <Field 
                          name="address"
                          as="textarea"
                          className="form-control"
                          placeholder="Address"
                        />
                        <ErrorMessage name="address" component="div" className="text-danger" />
                      </Col>
                    </Row>
                    
                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="country">Select Country *</label>
                        <Select
                          options={countries.map(country => ({
                            value: country.value,
                            label: country.label,
                            id: country.id,
                          }))}
                          value={(formikProps.values.country && countries.length)
                            ? { value: formikProps.values.country, label: countries.find(c => c.value === formikProps.values.country)?.label || '' }
                            : null}
                          onChange={(option) => handleCountryChange(option, formikProps)}
                          className="basic-select"
                          classNamePrefix="select"
                          placeholder="Canada"
                        />
                        <ErrorMessage name="country" component="div" className="text-danger" />
                      </Col>
                      <Col md={6}>
                        <label htmlFor="state">Select State *</label>
                        <Select
                          options={states.map(state => ({
                            value: state.value,
                            label: state.label
                          }))}
                          value={(formikProps.values.state && states.length)
                            ? { value: formikProps.values.state, label: states.find(s => s.value === formikProps.values.state)?.label || '' }
                            : null}
                          onChange={(option) => {
                            if (option) {
                              formikProps.setFieldValue('state', option.value);
                            }
                          }}
                          isDisabled={states.length === 0}
                          className="basic-select"
                          classNamePrefix="select"
                          placeholder="Select State"
                        />
                        <ErrorMessage name="state" component="div" className="text-danger" />
                      </Col>
                    </Row>
                    
                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="city">City *</label>
                        <Field 
                          name="city"
                          type="text"
                          className="form-control"
                          placeholder="City"
                        />
                        <ErrorMessage name="city" component="div" className="text-danger" />
                      </Col>
                      <Col md={6}>
                        <label htmlFor="postalCode">Postal Code/ZIP Code *</label>
                        <Field 
                          name="postalCode"
                          type="text"
                          className="form-control"
                          placeholder="Postal Code/ZIP Code"
                        />
                        <ErrorMessage name="postalCode" component="div" className="text-danger" />
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={12}>
                        <label htmlFor="purpose">Purpose of Donation *</label>
                        <Field 
                          name="purpose"
                          as="textarea"
                          className="form-control"
                          placeholder="Search & Select Donation for"
                        />
                        <ErrorMessage name="purpose" component="div" className="text-danger" />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
              
              {/* Credit Card Details Card */}
              <Col md={6}>
                <Card className="mb-4 elevated-card">
                  <Card.Header>Please Enter Credit Card Details</Card.Header>
                  <Card.Body>
                    <Row className="mb-3">
                      <Col md={12}>
                        <label htmlFor="paymentPurpose">This payment is for *</label>
                        <Select
                          options={[
                            { value: 'General Donation', label: 'General Donation' },
                            { value: 'Temple Construction', label: 'Temple Construction' },
                            { value: 'Community Service', label: 'Community Service' }
                          ]}
                          onChange={(option) => {
                            if (option) {
                              formikProps.setFieldValue('paymentPurpose', option.value);
                            }
                          }}
                          className="basic-select"
                          classNamePrefix="select"
                          placeholder="Search & Select Donation for"
                        />
                        <ErrorMessage name="paymentPurpose" component="div" className="text-danger" />
                      </Col>
                    </Row>
                    
                    <Row className="mb-3">
                      <Col md={12}>
                        <label htmlFor="donationAmount">Donation Amount *</label>
                        <Field name="donationAmount">
                          {({ field }: any) => (
                            <input
                              {...field}
                              type="text"
                              inputMode="decimal"
                              className="form-control"
                              placeholder="Donation Amount"
                              onChange={(e) => {
                                let v = e.target.value;
                                // Allow only digits and one dot, trim to 2 decimals
                                v = v.replace(/[^0-9.]/g, '');
                                const parts = v.split('.');
                                if (parts.length > 2) {
                                  v = parts[0] + '.' + parts.slice(1).join('');
                                }
                                const [intp, decp = ''] = v.split('.');
                                const dec2 = decp.slice(0, 2);
                                v = decp.length ? `${intp}.${dec2}` : intp;
                                formikProps.setFieldValue('donationAmount', v);
                              }}
                            />
                          )}
                        </Field>
                        <ErrorMessage name="donationAmount" component="div" className="text-danger" />
                      </Col>
                    </Row>
                    
                    <Row className="mb-3">
                      <Col md={12}>
                        <label htmlFor="donationType">Select Donation Type *</label>
                        <Select
                          options={[
                            { value: 'OneTime', label: 'OneTime' },
                            { value: 'Recurring', label: 'Recurring' }
                          ]}
                          onChange={(option) => {
                            if (option) {
                              formikProps.setFieldValue('donationType', option.value);
                            }
                          }}
                          defaultValue={{ value: 'OneTime', label: 'OneTime' }}
                          className="basic-select"
                          classNamePrefix="select"
                        />
                        <ErrorMessage name="donationType" component="div" className="text-danger" />
                        <label htmlFor="cardNumber">Card Number (13–19 digits) *</label>
                        <Field name="cardNumber">
                          {({ field }: any) => (
                            <input
                              {...field}
                              type="text"
                              className="form-control"
                              placeholder="Enter card number"
                              inputMode="numeric"
                              maxLength={19}
                              onChange={(e) => {
                                const digits = e.target.value.replace(/\D/g, '').slice(0, 19);
                                formikProps.setFieldValue('cardNumber', digits);
                              }}
                            />
                          )}
                        </Field>
                        <ErrorMessage name="cardNumber" component="div" className="text-danger" />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="cvv">CVV/CVC *</label>
                        <Field 
                          name="cvv"
                          type="password"
                          className="form-control"
                          placeholder="CVV"
                          maxLength={4}
                        />
                        <ErrorMessage name="cvv" component="div" className="text-danger" />
                      </Col>
                      <Col md={6}>
                        <label htmlFor="expirationDate">Expiration MM/YYYY *</label>
                        <Field name="expirationDate">
                          {({ field }: any) => (
                            <input
                              {...field}
                              type="text"
                              className="form-control"
                              placeholder="MM/YYYY"
                              maxLength={7}
                              onChange={(e) => {
                                let v = e.target.value;
                                // Keep only digits
                                v = v.replace(/\D/g, '');
                                // Limit to 6 digits MMYYYY
                                if (v.length > 6) v = v.slice(0, 6);
                                // Auto-insert slash after MM when at least 3 digits typed
                                if (v.length >= 3) {
                                  v = v.slice(0, 2) + '/' + v.slice(2);
                                }
                                formikProps.setFieldValue('expirationDate', v);
                              }}
                            />
                          )}
                        </Field>
                        <ErrorMessage name="expirationDate" component="div" className="text-danger" />
                      </Col>
                    </Row>
                    
                   
                    
                    {/* Google reCAPTCHA */}
                    <div className="mb-3">
                      <div id="recaptcha-container"></div>
                      {!recaptchaToken && (
                        <div className="text-danger mt-2">Please complete reCAPTCHA</div>
                      )}
                    </div>
                    <div className="text-end mt-3">
                      <Button 
                        type="submit" 
                        variant="success" 
                        disabled={loading || !recaptchaToken}
                        className="btn-submit btn-lg shadow-sm"
                      >
                        {loading ? 'Processing...' : 'Submit'}
                      </Button>
                      {loading && (
                        <div className="mt-3 d-flex align-items-center justify-content-end">
                          <Spinner animation="border" role="status" size="sm" className="me-2">
                            <span className="visually-hidden">Processing...</span>
                          </Spinner>
                          <span>Please wait. Your payment and email are being processed...</span>
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            {/* Submission Status Alert */}
            {submissionStatus && (
              <div className={`alert ${submissionStatus.success ? 'alert-success' : 'alert-danger'} mt-3`}>
                {submissionStatus.message}
              </div>
            )}
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default DonationForm;
